import React, { useState, useCallback, useEffect } from 'react';
import { sdk } from '@farcaster/frame-sdk';

/**
 * CRITICAL: Farcaster Quick Auth Component with Auth Address Support
 * 
 * Implements the official Farcaster Quick Auth flow using the @farcaster/frame-sdk
 * as documented at: https://miniapps.farcaster.xyz/docs/sdk/actions/quick-auth
 * 
 * Enhanced with Auth Address standard support for seamless web sign-in:
 * - Uses signed JWTs from Farcaster Quick Auth Server
 * - Works in both Mini App and regular web contexts
 * - Supports Auth Addresses for multi-device authentication
 * - Provides better error handling and performance
 * - No dependency on Neynar API keys or configuration
 * 
 * Benefits:
 * - Asymmetrically signed tokens (can be verified locally)
 * - Edge-deployed auth service (faster worldwide)
 * - Built-in security and session management
 * - Auth Address support for web browsers
 * - Compatible with FIP-225 standard
 * 
 * Reference: https://github.com/farcasterxyz/protocol/discussions/225
 */

// Global error handler for Farcaster SDK background errors
if (typeof window !== 'undefined') {
  const originalConsoleError = console.error;
  console.error = (...args) => {
    const message = args.join(' ');
    
    // Suppress known Farcaster API connectivity errors that don't affect functionality
    if (message.includes('client.farcaster.xyz') && 
        (message.includes('ERR_CONNECTION_CLOSED') || 
         message.includes('Failed to fetch') ||
         message.includes('unseen'))) {
      console.warn('⚠️ Farcaster background API call failed (non-critical):', message);
      return;
    }
    
    // Let all other errors through
    originalConsoleError.apply(console, args);
  };
}

const FarcasterQuickAuth = ({ 
  onSuccess, 
  onError,
  customStyles = {},
  buttonText = "🎯 Sign in with Farcaster",
  className = ""
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);

  // Check for existing authentication on mount
  useEffect(() => {
    const checkExistingAuth = () => {
      try {
        // First check if MiniApp auth is active (takes priority)
        const miniAppUser = localStorage.getItem('farcaster_miniapp_user');
        if (miniAppUser) {
          console.log('🏰 MiniApp auth detected - QuickAuth will defer to MiniApp authentication');
          return; // Don't proceed with QuickAuth if MiniApp auth is active
        }
        
        const storedToken = localStorage.getItem('farcaster_auth_token');
        const storedUser = localStorage.getItem('farcaster_user');
        
        if (storedToken && storedUser) {
          const userData = JSON.parse(storedUser);
          const tokenData = JSON.parse(atob(storedToken.split('.')[1])); // Decode JWT payload
          
          // Check if token is still valid (not expired)
          const now = Math.floor(Date.now() / 1000);
          if (tokenData.exp && tokenData.exp > now) {
            console.log('✅ Valid Farcaster QuickAuth found, restoring session');
            setUser(userData);
            setIsAuthenticated(true);
            if (onSuccess) onSuccess(userData, storedToken);
            return;
          } else {
            console.log('⚠️ Farcaster QuickAuth token expired, clearing storage');
            localStorage.removeItem('farcaster_auth_token');
            localStorage.removeItem('farcaster_user');
          }
        }
      } catch (error) {
        console.warn('⚠️ Error checking existing Farcaster QuickAuth:', error);
        // Clear potentially corrupted data
        localStorage.removeItem('farcaster_auth_token');
        localStorage.removeItem('farcaster_user');
      }
    };

    checkExistingAuth();
  }, [onSuccess]);

  // Don't render anything if MiniApp auth is active
  useEffect(() => {
    const miniAppUser = localStorage.getItem('farcaster_miniapp_user');
    if (miniAppUser) {
      console.log('🏰 MiniApp user detected - QuickAuth component will not render');
      return;
    }
  }, []);

  // Early return if MiniApp auth is active
  const miniAppUser = localStorage.getItem('farcaster_miniapp_user');
  if (miniAppUser) {
    return null; // Don't render anything if user is already authenticated via MiniApp
  }

  const handleSignIn = useCallback(async () => {
    try {
      // Check if MiniApp auth is already active
      const miniAppUser = localStorage.getItem('farcaster_miniapp_user');
      if (miniAppUser) {
        console.log('🏰 MiniApp authentication already active - QuickAuth not needed');
        return; // Don't proceed with QuickAuth if MiniApp auth is active
      }
      
      setIsLoading(true);
      console.log('🚀 Starting Farcaster Quick Auth...');

      // Check if we're in a Farcaster client or web browser
      const isFarcasterClient = window.location.hostname.includes('warpcast') || 
                               window.navigator.userAgent.includes('Farcaster') ||
                               window.parent !== window; // In iframe/miniapp

      console.log('🌐 Environment:', isFarcasterClient ? 'Farcaster Client' : 'Web Browser');

      // Enhanced detection and handling for web browsers
      if (!isFarcasterClient) {
        // In web browsers, Farcaster Quick Auth typically fails
        console.warn('⚠️ Web browser detected - Farcaster Quick Auth has limited support');
        throw new Error('WEB_BROWSER_NOT_SUPPORTED');
      }

      // Enhanced timeout and retry logic for Farcaster clients
      const quickAuthWithTimeout = async (timeoutMs = 15000) => {
        try {
          // First check if SDK is properly loaded
          if (!sdk || !sdk.actions || !sdk.actions.quickAuth) {
            throw new Error('FARCASTER_SDK_NOT_READY');
          }

          return await Promise.race([
            sdk.actions.quickAuth({
              // Enhanced options for Farcaster client support
              supportAuthAddresses: true, // Enable Auth Address support
              redirectTo: window.location.origin, // Better redirect handling
            }),
            new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Authentication timeout')), timeoutMs)
            )
          ]);
        } catch (error) {
          console.log('🔍 QuickAuth error details:', error);
          
          // Handle specific SDK errors
          if (error.message && error.message.includes('result')) {
            throw new Error('FARCASTER_SDK_ERROR');
          } else if (error.message && (error.message.includes('timeout') || error.message.includes('Authentication timeout'))) {
            throw new Error('CONNECTION_TIMEOUT');
          } else if (error.message && error.message.includes('not ready')) {
            throw new Error('FARCASTER_SDK_NOT_READY');
          }
          throw error;
        }
      };

      // Request signed JWT from Farcaster Quick Auth Server with timeout
      const { token } = await quickAuthWithTimeout();
      
      console.log('✅ Received Farcaster auth token');
      
      // Decode the JWT to get user information
      const payload = JSON.parse(atob(token.split('.')[1]));
      console.log('📊 JWT payload:', payload);
      
      // Extract user data from JWT with Auth Address support
      const userData = {
        fid: payload.sub, // FID of the signed in user
        address: payload.address, // Address the user signed in with (could be auth address)
        username: payload.username || `fid-${payload.sub}`, // Username if available
        displayName: payload.displayName || `User ${payload.sub}`,
        pfpUrl: payload.pfpUrl || null,
        isAuthenticated: true,
        authenticatedAt: new Date().toISOString(),
        tokenExpiresAt: new Date(payload.exp * 1000).toISOString(),
        // Auth Address specific fields
        isAuthAddress: payload.authAddress ? true : false,
        custodyAddress: payload.custodyAddress || payload.address,
        authIndex: payload.authIndex || 0, // For reinstatable auth addresses
        environment: isFarcasterClient ? 'farcaster' : 'web'
      };
      
      console.log('👤 Processed user data:', userData);
      
      // Store authentication data
      localStorage.setItem('farcaster_auth_token', token);
      localStorage.setItem('farcaster_user', JSON.stringify(userData));
      
      // Update component state
      setUser(userData);
      setIsAuthenticated(true);
      
      // Dispatch event for other components
      window.dispatchEvent(new CustomEvent('farcaster-auth-update', { 
        detail: { user: userData, token } 
      }));
      
      console.log('🎉 Farcaster authentication successful!');
      
      // Call success callback
      if (onSuccess) {
        onSuccess(userData, token);
      }
      
    } catch (error) {
      // Handle specific error types with more descriptive messages
      let errorMessage = 'Authentication failed';
      let shouldLogError = true;
      
      if (error.message === 'WEB_BROWSER_NOT_SUPPORTED') {
        // Expected behavior for web browsers
        console.warn('⚠️ Web browser detected - this is expected behavior');
        errorMessage = 'WEB_BROWSER_NOT_SUPPORTED';
        shouldLogError = false;
      } else if (error.message === 'FARCASTER_SDK_ERROR') {
        // SDK specific error (likely the 'result' undefined error)
        console.warn('⚠️ Farcaster SDK error - likely web browser incompatibility');
        errorMessage = 'WEB_BROWSER_NOT_SUPPORTED';
        shouldLogError = false;
      } else if (error.message === 'CONNECTION_TIMEOUT') {
        // Connection timeout - network issue
        console.warn('⚠️ Network connectivity issue with Farcaster API:', error.message);
        errorMessage = 'Authentication timed out - please check your connection and try again';
        shouldLogError = false;
      } else if (error.message === 'FARCASTER_SDK_NOT_READY') {
        // SDK not ready yet
        console.warn('⚠️ Farcaster SDK not ready - may need to wait for initialization');
        errorMessage = 'Farcaster SDK is loading - please try again in a moment';
        shouldLogError = false;
      } else if (error.message && error.message.includes('result')) {
        // The specific error we're seeing in web browsers
        console.warn('⚠️ Farcaster SDK result error - web browser detected');
        errorMessage = 'WEB_BROWSER_NOT_SUPPORTED';
        shouldLogError = false;
      } else if (error.message) {
        if (error.message.includes('timeout')) {
          errorMessage = 'Authentication timed out - please check your connection and try again';
        } else if (error.message.includes('Failed to fetch') || error.message.includes('CONNECTION_CLOSED')) {
          errorMessage = 'Network connection issue - please try again in a moment';
        } else if (error.message.includes('cancelled')) {
          errorMessage = 'Authentication was cancelled';
        } else if (error.message.includes('not supported')) {
          errorMessage = 'WEB_BROWSER_NOT_SUPPORTED';
          shouldLogError = false;
        } else if (error.message.includes('SDK not ready')) {
          errorMessage = 'Farcaster SDK is not ready. Please refresh the page and try again.';
        } else {
          errorMessage = error.message;
        }
      } else if (error.code === 'USER_CANCELLED') {
        errorMessage = 'Authentication was cancelled';
      } else if (error.code === 'NETWORK_ERROR') {
        errorMessage = 'Network error - please check your connection and try again';
      } else if (error.code === 'AUTH_ADDRESS_NOT_SUPPORTED') {
        errorMessage = 'Auth Address authentication is not supported in this environment';
      }
      
      // Log errors only if they're unexpected
      if (shouldLogError) {
        console.error('❌ Farcaster Quick Auth failed:', error);
      }
      
      // Don't show intrusive errors for network issues - log them instead
      if (errorMessage.includes('Network') || errorMessage.includes('connection')) {
        console.warn('⚠️ Network connectivity issue with Farcaster API:', errorMessage);
        // Show a less alarming message to user
        errorMessage = 'Connection issue - authentication may be temporarily unavailable';
      }
      
      // Call error callback only for non-web-browser errors
      if (onError && errorMessage !== 'WEB_BROWSER_NOT_SUPPORTED') {
        onError(new Error(errorMessage));
      }
      
    } finally {
      setIsLoading(false);
    }
  }, [onSuccess, onError]);

  const handleSignOut = useCallback(() => {
    console.log('🚪 Signing out of Farcaster...');
    
    // Clear storage
    localStorage.removeItem('farcaster_auth_token');
    localStorage.removeItem('farcaster_user');
    
    // Reset state
    setUser(null);
    setIsAuthenticated(false);
    
    // Dispatch event
    window.dispatchEvent(new CustomEvent('farcaster-auth-update', { 
      detail: { user: null, token: null } 
    }));
    
    console.log('✅ Farcaster sign out complete');
  }, []);

  // If already authenticated, show user info and sign out option
  if (isAuthenticated && user) {
    return (
      <div className={`flex items-center space-x-3 ${className}`} style={customStyles}>
        <div className="flex items-center space-x-2 text-sm text-secondary-300">
          <div className="w-2 h-2 bg-green-400 rounded-full"></div>
          <span>@{user.username}</span>
          {user.address && (
            <span className="text-xs text-secondary-500 font-mono">
              {user.address.slice(0, 6)}...{user.address.slice(-4)}
            </span>
          )}
          <span className="text-xs text-secondary-500">via Farcaster</span>
        </div>
        <button
          onClick={handleSignOut}
          className="text-xs text-secondary-400 hover:text-white transition-colors"
          title="Sign out"
        >
          🚪
        </button>
      </div>
    );
  }

  // Check environment for better UX - be more precise about Farcaster detection
  const isFarcasterClient = typeof window !== 'undefined' && (
    window.location.hostname.includes('warpcast') || 
    window.navigator.userAgent.includes('Warpcast') ||
    window.navigator.userAgent.includes('Farcaster') ||
    (window.parent !== window && document.referrer.includes('farcaster')) ||
    (window.parent !== window && document.referrer.includes('warpcast'))
  );

  // Don't show "Open in Warpcast" if we're actually in a Farcaster client already
  // Show normal sign in button instead
  return (
    <button
      onClick={handleSignIn}
      disabled={isLoading}
      className={`
        ${className || 'px-4 py-2 bg-farcaster-600 hover:bg-farcaster-500 text-white rounded-lg font-medium transition-colors'}
        ${isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-lg'}
        flex items-center space-x-2
      `}
      style={customStyles}
    >
      {isLoading ? (
        <>
          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          <span>Authenticating...</span>
        </>
      ) : (
        <span>{buttonText}</span>
      )}
    </button>
  );
};

/**
 * Hook to get current Farcaster Quick Auth status
 * Returns user data and token if authenticated
 */
export const useFarcasterQuickAuth = () => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Function to check authentication status
  const checkAuth = useCallback(() => {
    try {
      // First check if MiniApp auth is active (takes priority)
      const miniAppUser = localStorage.getItem('farcaster_miniapp_user');
      if (miniAppUser) {
        const userData = JSON.parse(miniAppUser);
        // Return MiniApp user data but mark it as non-QuickAuth
        setUser(userData);
        setToken(null); // MiniApp doesn't use the same token system
        return { user: userData, token: null, isAuthenticated: true };
      }
      
      const storedToken = localStorage.getItem('farcaster_auth_token');
      const storedUser = localStorage.getItem('farcaster_user');
      
      if (storedToken && storedUser) {
        const userData = JSON.parse(storedUser);
        const tokenData = JSON.parse(atob(storedToken.split('.')[1]));
        
        // Check if token is still valid
        const now = Math.floor(Date.now() / 1000);
        if (tokenData.exp && tokenData.exp > now) {
          setUser(userData);
          setToken(storedToken);
          return { user: userData, token: storedToken, isAuthenticated: true };
        } else {
          // Token expired
          localStorage.removeItem('farcaster_auth_token');
          localStorage.removeItem('farcaster_user');
          setUser(null);
          setToken(null);
        }
      }
      
      setUser(null);
      setToken(null);
      return { user: null, token: null, isAuthenticated: false };
    } catch (error) {
      console.warn('Error checking Farcaster auth:', error);
      setUser(null);
      setToken(null);
      return { user: null, token: null, isAuthenticated: false };
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Check auth on mount
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Listen for auth updates
  useEffect(() => {
    const handleAuthUpdate = (event) => {
      const { user: newUser, token: newToken } = event.detail;
      setUser(newUser);
      setToken(newToken);
    };

    window.addEventListener('farcaster-auth-update', handleAuthUpdate);
    return () => window.removeEventListener('farcaster-auth-update', handleAuthUpdate);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('farcaster_auth_token');
    localStorage.removeItem('farcaster_user');
    setUser(null);
    setToken(null);
    window.dispatchEvent(new CustomEvent('farcaster-auth-update', { 
      detail: { user: null, token: null } 
    }));
  }, []);

  return {
    user,
    token,
    isAuthenticated: !!user, // User can be authenticated via Mini App (no token) or Quick Auth (with token)
    isLoading,
    logout,
    checkAuth
  };
};

export default FarcasterQuickAuth; 